import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const MarketContext = createContext();

const REDIRECT_RULES = [
  { match: /^\/stocks\/.+/, target: "/dashboard" },
  { match: /^\/portfolio\/.+/, target: "/allPortfolio" },
  { match: /^\/settings.*/, target: "/dashboard" },
];

const TRANSITION_KEY = "marketTransitioning";

const getRedirectTarget = (pathname) => {
  const rule = REDIRECT_RULES.find((r) => r.match.test(pathname));
  return rule ? rule.target : null;
};

export const MarketProvider = ({ children }) => {
  const [market, setMarketState] = useState(
    () => sessionStorage.getItem("selectedMarket") ?? "US"
  );

  // On mount: if a reload-transition was in progress, show overlay briefly
  // then clear the flag so normal navigation doesn't re-trigger it.
  const [transitioning, setTransitioning] = useState(
    () => sessionStorage.getItem(TRANSITION_KEY) === "1"
  );

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // After a reload the flag is still set — clear it after a short paint delay
  useEffect(() => {
    if (!transitioning) return;
    const id = setTimeout(() => {
      sessionStorage.removeItem(TRANSITION_KEY);
      setTransitioning(false);
    }, 600); // enough time for the page to render visibly before hiding overlay
    return () => clearTimeout(id);
  }, []); // intentionally runs once on mount only

  const setMarket = (newMarket) => {
    if (newMarket === market) return;

    sessionStorage.setItem("selectedMarket", newMarket);
    setMarketState(newMarket);
    setTransitioning(true);

    const hasQuery = search && search.length > 1;
    const target = getRedirectTarget(pathname);

    sessionStorage.setItem(TRANSITION_KEY, "1");
    setTimeout(() => {
      if (target) {
        window.location.href = target;           // rule-based redirect
      } else if (hasQuery) {
        window.location.href = pathname;         // same page, no query string
      } else {
        window.location.reload();               // same page, no query string
      }
    }, 1800);
  };

  return (
    <MarketContext.Provider value={{ market, setMarket }}>
      {children}
      {transitioning && <MarketTransitionOverlay market={market} />}
    </MarketContext.Provider>
  );
};

/* ── Fullscreen overlay ───────────────────────────────────────────────────── */
const MarketTransitionOverlay = ({ market }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 99999,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "var(--spacing-md)",
      backgroundColor: "var(--color-bg-overlay)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        border: "3px solid var(--color-border-primary)",
        borderTopColor: "var(--color-primary)",
        animation: "market-spin 0.8s linear infinite",
      }}
    />

    <div style={{ textAlign: "center" }}>
      <p style={{
        color: "var(--color-text-primary)",
        fontSize: "var(--font-size-lg)",
        fontWeight: "var(--font-weight-bold)",
        margin: 0,
      }}>
        Switching market…
      </p>
      <p style={{
        color: "var(--color-text-muted)",
        fontSize: "var(--font-size-sm)",
        fontWeight: "var(--font-weight-medium)",
        margin: "var(--spacing-xs) 0 0",
      }}>
        Loading data for{" "}
        <span style={{ color: "var(--color-primary)", fontWeight: "var(--font-weight-semibold)" }}>
          {market}
        </span>
      </p>
    </div>

    <style>{`
      @keyframes market-spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export const useMarket = () => useContext(MarketContext);